import React, { useState, useEffect, useMemo } from "react";
import { useSkinsMasterContext } from "./SkinsMaster.jsx";
import { dec, nils, tofeth, toeth } from "../../utils/utils.jsx";
import ERC721base from "../../contracts/v2/ERC721base.js";
import ERC20base from "../../contracts/v2/ERC20base.js";
import { skins_rvmode_addrmap } from "../../contracts/v2/extra_logs_parsers.js";
import { contractAddress_list } from "../../contracts/contracts.jsx";
import { ethers } from "ethers";
import { Modal } from "../../components/Modal.jsx";
import { twMerge } from "tailwind-merge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { Zap, TrendingUp } from "lucide-react";
import { TokenIcon } from "../../components/utilityComps.jsx";

// Enhanced Token Allowance Modal Content
const TokenAllowanceModalContent = ({
  title,
  token,
  allowance_target,
  auto_amount_fn,
  on_close,
  vault,
  ciparams,
}) => {
  const [alw, set_alw] = useState(null);
  const [balance, set_balance] = useState(null);
  const [show_input, set_show_input] = useState(false);
  const [input_amount, set_input_amount] = useState("");
  const [loading, set_loading] = useState(true);

  const get_token_styles = (token) => {
    // Use consistent r3 theme colors instead of bright glows
    return {
      gradient_text: "text-white",
      gradient_bg: "bg-r3dark/30",
      hover_bg: "hover:bg-r3lig/20",
      solid_bg: "bg-r3dark/20 hover:bg-r3lig/10",
      border: "border-r3lig/30 hover:border-r3lig/50",
      shadow: "",
      glow: "",
    };
  };

  const token_styles = get_token_styles(token);

  const get_allowance = async () => {
    try {
      const con = await ERC20base.get_contract(token, ciparams);
      const alw = await con.allowance(vault, allowance_target);
      return tofeth(alw);
    } catch (err) {
      console.error("Failed to get allowance:", err);
      return null;
    }
  };

  const get_balance = async () => {
    try {
      const con = await ERC20base.get_contract(token, ciparams);
      const bal = await con.balanceOf(vault);
      return tofeth(bal);
    } catch (err) {
      console.error("Failed to get balance:", err);
      return null;
    }
  };

  const upd_alw = async () => {
    set_loading(true);
    const [a, b] = await Promise.all([get_allowance(), get_balance()]);
    set_alw(a);
    set_balance(b);
    set_loading(false);
  };

  useEffect(() => {
    if (!vault || !ciparams) return;
    upd_alw();
  }, [vault, allowance_target]);

  const set_allowance = async (amount) => {
    try {
      const con = await ERC20base.get_contract(token, ciparams);
      await con.approve(allowance_target, toeth(amount));
      await upd_alw();
      set_show_input(false);
      set_input_amount("");
    } catch (err) {
      console.error("Failed to set allowance:", err);
    }
  };

  const revoke_allowance = async () => {
    await set_allowance(0);
  };

  const handle_add_allowance = () => {
    set_show_input(true);
    set_input_amount("");
  };

  const handle_auto_amount = () => {
    if (balance && auto_amount_fn) {
      try {
        let auto_amount = auto_amount_fn();
        if (parseFloat(auto_amount) > parseFloat(balance)) {
          auto_amount = parseFloat(balance) * 0.95;
        }
        const truncated_amount = dec(auto_amount, token === "WETH" ? 4 : 0);
        set_input_amount(truncated_amount.toString());
      } catch (err) {
        console.error("Auto amount function error:", err);
        set_input_amount((parseFloat(balance) * 0.7).toString());
      }
    }
  };

  const handle_max_amount = () => {
    if (balance) {
      let max_amount = parseFloat(balance) * 0.99; // Leave small buffer
      const truncated_amount = dec(max_amount, token === "WETH" ? 4 : 0);
      set_input_amount(truncated_amount.toString());
    }
  };

  const handle_custom_allowance = async () => {
    if (!input_amount || parseFloat(input_amount) <= 0) return;
    if (balance && parseFloat(input_amount) > parseFloat(balance)) {
      alert("Amount cannot exceed your balance");
      return;
    }
    await set_allowance(input_amount);
  };

  return (
    <div className="relative bg-r3dark/20 border border-r3lig/30 backdrop-blur-md rounded-xl overflow-hidden">
      {/* Header */}
      <div className="relative p-4 border-b border-r3lig/30">
        <div className="text-center">
          <h2 className="text-lg font-digi text-white mb-2">{title}</h2>
          <div className="h-px bg-gradient-to-r from-transparent via-r3lig/50 to-transparent"></div>
        </div>

        <div className="flex items-center justify-center gap-4 mt-4">
          <TokenIcon token={token} size="md" />
          <div>
            <p className="text-2xl font-digi font-bold tracking-wide text-white">
              {token}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative p-6 space-y-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-r3reg mb-4"></div>
            <div className="text-gray-300 text-lg">
              Loading allowance data...
            </div>
          </div>
        ) : (
          <>
            {/* Allowance and Balance Info */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 p-4 rounded-xl border border-r3lig/30 bg-r3dark/20">
                <div className="flex items-center gap-2">
                  <span className="text-gray-200 font-medium">
                    Current Allowance:
                  </span>
                  <button
                    onClick={upd_alw}
                    className="p-2 rounded-lg transition-all duration-200 border bg-r3reg/10 hover:bg-r3reg/20 border-r3reg/30 hover:border-r3reg/50 text-r3reg hover:text-white"
                  >
                    <FontAwesomeIcon icon={faSyncAlt} className="text-sm" />
                  </button>
                </div>
                {nils(alw) ? (
                  <span className="text-gray-400 text-lg">--</span>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-lg text-white">
                      {dec(alw, token === "WETH" ? 4 : 0)}
                    </span>
                    <span className="text-gray-300 font-medium">{token}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center gap-3 p-3 rounded-lg bg-r3dark/10 border border-r3lig/20">
                <span className="text-gray-300">Your Balance:</span>
                {nils(balance) ? (
                  <span className="text-gray-400">--</span>
                ) : (
                  <div className="flex items-center gap-1">
                    <span className="text-white font-semibold">
                      {dec(balance, token === "WETH" ? 4 : 0)}
                    </span>
                    <span className="text-gray-300">{token}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {!show_input ? (
              <div className="flex gap-2">
                <button
                  onClick={handle_add_allowance}
                  className="flex-1 px-6 py-3 rounded-xl font-semibold transition-all duration-200 border text-white bg-r3reg hover:bg-r3dark border-r3lig/30 hover:border-r3lig/50"
                >
                  Add Allowance
                </button>
                <button
                  onClick={revoke_allowance}
                  className="flex-1 px-6 py-3 rounded-xl font-semibold transition-all duration-200 border bg-red-600/20 hover:bg-red-600/30 border-red-500/30 hover:border-red-500/50 text-red-300 hover:text-white"
                >
                  Revoke All
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-gray-200 font-medium mb-4 text-center">
                  Enter allowance amount:
                </div>

                <div className="mb-4 relative">
                  <input
                    type="number"
                    value={input_amount}
                    onChange={(e) => set_input_amount(e.target.value)}
                    placeholder="0.0"
                    className="w-full bg-r3dark/40 border border-r3lig/30 rounded-xl px-4 py-3 text-white text-center text-lg font-mono pr-24 focus:outline-none focus:border-r3lig/50 transition-all duration-200"
                    max={balance || 0}
                    min="0"
                    step="any"
                  />
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex gap-1">
                    <button
                      onClick={handle_auto_amount}
                      className="px-2 py-1 rounded-md text-xs font-medium transition-all duration-200 border bg-r3reg/20 hover:bg-r3reg/30 border-r3reg/30 hover:border-r3reg/50 text-r3reg hover:text-white"
                    >
                      Auto
                    </button>
                    <button
                      onClick={handle_max_amount}
                      className="px-2 py-1 rounded-md text-xs font-medium transition-all duration-200 border bg-r3lig/20 hover:bg-r3lig/30 border-r3lig/30 hover:border-r3lig/50 text-gray-300 hover:text-white"
                    >
                      Max
                    </button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      set_show_input(false);
                      set_input_amount("");
                    }}
                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded text-sm transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handle_custom_allowance}
                    className="flex-1 bg-green-600/20 hover:bg-green-600/30 border border-green-500/30 hover:border-green-500/50 text-white px-4 py-2 rounded text-sm transition-colors duration-200"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Enhanced NFT Approval Modal Content
const NFTApprovalModalContent = ({
  title,
  token,
  token_addr,
  contract_addr,
  on_close,
  vault,
  ciparams,
}) => {
  const [apv, set_apv] = useState(null);
  const [loading, set_loading] = useState(true);

  const get_token_styles = (token) => {
    // Use consistent r3 theme colors for NFT modals too
    return {
      gradient_text: "text-white",
      gradient_bg: "bg-r3dark/30",
      hover_bg: "hover:bg-r3lig/20",
      solid_bg: "bg-r3dark/20 hover:bg-r3lig/10",
      border: "border-r3lig/30 hover:border-r3lig/50",
      shadow: "",
    };
  };

  const token_styles = get_token_styles(token);

  const upd_apv = async () => {
    try {
      set_loading(true);
      const con = await ERC721base.get_contract(token_addr, ciparams);
      const a = await con.isApprovedForAll(vault, contract_addr);
      set_apv(a);
    } catch (err) {
      console.error("Failed to check approval:", err);
    } finally {
      set_loading(false);
    }
  };

  useEffect(() => {
    if (!vault || !ciparams) return;
    upd_apv();
  }, [vault, token_addr]);

  const set_approval = async (set_to) => {
    try {
      const con = await ERC721base.get_contract(token_addr, ciparams);
      await con.setApprovalForAll(contract_addr, set_to);
      await upd_apv();
    } catch (err) {
      console.error("Failed to set approval:", err);
    }
  };

  return (
    <div className="relative bg-r3dark/20 border border-r3lig/30 backdrop-blur-md rounded-xl overflow-hidden">
      {/* Header */}
      <div className="relative p-4 border-b border-r3lig/30">
        <div className="text-center">
          <h2 className="text-lg font-digi text-white mb-2">{title}</h2>
          <div className="h-px bg-gradient-to-r from-transparent via-r3lig/50 to-transparent"></div>
        </div>

        <div className="flex items-center justify-center gap-4 mt-4">
          <TokenIcon token={token.toUpperCase()} size="md" />
          <div>
            <p className="text-2xl font-digi font-bold tracking-wide capitalize text-white">
              {token} NFT
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative p-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-r3reg mb-4"></div>
            <div className="text-gray-300 text-lg">
              Loading approval status...
            </div>
          </div>
        ) : (
          <>
            {/* Approval Status Display */}
            <div className="flex items-center justify-center gap-3 my-4 p-3 rounded-xl border border-r3lig/30 bg-r3dark/20">
              <span className="text-gray-200 font-medium">Status:</span>
              {nils(apv) ? (
                <span className="text-gray-400 text-sm">Loading...</span>
              ) : (
                <span
                  className={twMerge(
                    "font-semibold text-sm px-2 py-1 rounded-lg",
                    apv
                      ? "text-green-300 bg-green-500/20 border border-green-500/30"
                      : "text-red-300 bg-red-500/20 border border-red-500/30",
                  )}
                >
                  {apv ? "✓ Approved" : "✗ Not Approved"}
                </span>
              )}
              <button
                onClick={upd_apv}
                className="ml-2 p-2 rounded-lg transition-all duration-200 border bg-r3reg/10 hover:bg-r3reg/20 border-r3reg/30 hover:border-r3reg/50 text-r3reg hover:text-white"
              >
                <FontAwesomeIcon icon={faSyncAlt} />
              </button>
            </div>

            {/* Action Button */}
            <div className="flex justify-center mt-6">
              <button
                onClick={() => set_approval(!apv)}
                disabled={nils(apv)}
                className={twMerge(
                  "px-6 py-3 rounded-xl font-semibold transition-all duration-200 border",
                  nils(apv)
                    ? "bg-gray-500/20 border-gray-500/30 text-gray-400 cursor-not-allowed"
                    : apv
                      ? "bg-red-600/20 hover:bg-red-600/30 border-red-500/30 hover:border-red-500/50 text-red-300 hover:text-white"
                      : "bg-r3reg hover:bg-r3dark border-r3lig/30 hover:border-r3lig/50 text-white",
                )}
              >
                {nils(apv)
                  ? "Loading..."
                  : apv
                    ? "Revoke Approval"
                    : "Set Approval"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const PrerequisitesRows = () => {
  const {
    vault,
    isauvault,
    prereq,
    set_prereq,
    usd_to_tok_val,
    ciparams,
    _bal,
  } = useSkinsMasterContext();
  const [is_hidden, set_is_hidden] = useState(true);
  const [nft_approvals, set_nft_approvals] = useState({
    bike: null,
    car: null,
    horse: null,
  });
  const [loading_nft_checks, set_loading_nft_checks] = useState(false);
  const [token_allowances, set_token_allowances] = useState({
    SGC: 0,
    DEZ: 0,
    WETH: 0,
  });
  const [loading_token_checks, set_loading_token_checks] = useState(false);

  // Modal states
  const [show_token_modal, set_show_token_modal] = useState(null);
  const [show_nft_modal, set_show_nft_modal] = useState(null);

  // Target $50 USD equivalent in allowances
  const target_usd = 50;

  // Calculate recommended token amounts based on current USD prices
  const recommended_amounts = {
    SGC: 100, // SGC doesn't use USD conversion
    DEZ: usd_to_tok_val ? usd_to_tok_val(target_usd, "DEZ") : 1000,
    WETH: usd_to_tok_val ? usd_to_tok_val(target_usd, "WETH") : 0.02,
  };

  // Calculate token allowance status
  const get_token_allowance_status = (token) => {
    if (loading_token_checks) return "loading";
    const current = token_allowances[token];
    const needed = recommended_amounts[token];
    if (current >= needed) return "sufficient";
    if (current > 0) return "partial";
    return "none";
  };

  // Calculate NFT approval status
  const nft_approval_status = useMemo(() => {
    if (loading_nft_checks) return "loading";

    const approved_count = Object.values(nft_approvals).filter(Boolean).length;
    const total_count = Object.keys(skins_rvmode_addrmap).filter(
      (key) => skins_rvmode_addrmap[key],
    ).length;

    if (approved_count === 0) return "none";
    if (approved_count === total_count) return "sufficient";
    return "partial";
  }, [nft_approvals, loading_nft_checks]);

  // Check NFT approvals for all skin contracts
  useEffect(() => {
    const check_nft_approvals = async () => {
      if (!vault || !isauvault) return;

      set_loading_nft_checks(true);
      const approvals = {};

      try {
        // Check against DNA Pay Router for approvals
        const approval_target_address = contractAddress_list.dnapayrouter;

        for (const [rvmode, contract_address] of Object.entries(
          skins_rvmode_addrmap,
        )) {
          if (!contract_address) continue;

          try {
            const contract = await ERC721base.get_contract(
              contract_address,
              ciparams,
            );
            const is_approved = await contract.isApprovedForAll(
              vault,
              approval_target_address,
            );
            approvals[rvmode] = is_approved;
          } catch (error) {
            console.error(`Failed to check ${rvmode} approval:`, error);
            approvals[rvmode] = false;
          }
        }

        set_nft_approvals(approvals);
      } catch (error) {
        console.error("Failed to check NFT approvals:", error);
      } finally {
        set_loading_nft_checks(false);
      }
    };

    check_nft_approvals();
  }, [vault, isauvault]);

  // Check token allowances
  useEffect(() => {
    const check_token_allowances = async () => {
      if (!vault || !isauvault) return;

      set_loading_token_checks(true);
      const allowances = {};

      try {
        const spender_address = contractAddress_list.dnapayrouter;
        const tokens = ["SGC", "DEZ", "WETH"];

        for (const token of tokens) {
          try {
            const contract = await ERC20base.get_contract(token, ciparams);
            const allowance_wei = await contract.allowance(
              vault,
              spender_address,
            );
            allowances[token] = parseFloat(tofeth(allowance_wei));
          } catch (error) {
            console.error(`Failed to check ${token} allowance:`, error);
            allowances[token] = 0;
          }
        }

        set_token_allowances(allowances);
      } catch (error) {
        console.error("Failed to check token allowances:", error);
      } finally {
        set_loading_token_checks(false);
      }
    };

    check_token_allowances();
  }, [vault, isauvault]);

  const toggle_visibility = () => {
    set_is_hidden(!is_hidden);
  };

  // Don't show if no vault or not authenticated
  if (!vault || !isauvault) {
    return null;
  }

  // Prerequisites data structure with real allowance checks
  const prerequisites = {
    nft_approval: {
      current: Object.values(nft_approvals).filter(Boolean).length,
      needed: Object.keys(skins_rvmode_addrmap).filter((key) => skins_rvmode_addrmap[key])
        .length,
      status: nft_approval_status,
      details: nft_approvals,
    },
    token_allowances: {
      SGC: {
        current: token_allowances.SGC,
        needed: recommended_amounts.SGC,
        status: get_token_allowance_status("SGC"),
      },
      DEZ: {
        current: token_allowances.DEZ,
        needed: recommended_amounts.DEZ,
        status: get_token_allowance_status("DEZ"),
      },
      WETH: {
        current: token_allowances.WETH,
        needed: recommended_amounts.WETH,
        status: get_token_allowance_status("WETH"),
      },
    },
  };

  const get_status_indicator = (status) => {
    switch (status) {
      case "sufficient":
        return <span className="text-green-400">✅</span>;
      case "partial":
        return <span className="text-yellow-400">⚠️</span>;
      case "loading":
        return <span className="text-blue-400">🔄</span>;
      default:
        return <span className="text-red-400">❌</span>;
    }
  };

  const get_status_color = (status) => {
    switch (status) {
      case "sufficient":
        return "border-green-500/30 bg-green-500/10";
      case "partial":
        return "border-yellow-500/30 bg-yellow-500/10";
      case "loading":
        return "border-blue-500/30 bg-blue-500/10";
      default:
        return "border-red-500/30 bg-red-500/10";
    }
  };

  return (
    <>
      <div className="mb-8 md:mb-12">
        <div className="bg-r3dark/20 border border-r3lig/30 backdrop-blur-md rounded-2xl overflow-hidden">
          {/* Header with toggle */}
          <div
            className="p-4 flex justify-between items-center cursor-pointer hover:bg-r3lig/10 transition-colors duration-200"
            onClick={toggle_visibility}
          >
            <div>
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-digi text-white">Prerequisites</h3>
                <div className="flex space-x-1">
                  {get_status_indicator(prerequisites.nft_approval.status)}
                  {get_status_indicator(
                    prerequisites.token_allowances.SGC.status,
                  )}
                  {get_status_indicator(
                    prerequisites.token_allowances.DEZ.status,
                  )}
                  {get_status_indicator(
                    prerequisites.token_allowances.WETH.status,
                  )}
                </div>
              </div>
              <p className="text-gray-400 text-xs mt-1">
                It's advised to maintain a manageable limit for convenience,
                otherwise when it's required you will be prompted too
              </p>
            </div>
            <div className="text-gray-400">{is_hidden ? "▼" : "▲"}</div>
          </div>

          {/* Prerequisites Content */}
          {!is_hidden && (
            <div className="px-4 pb-4 space-y-3">
              {/* NFT Approval Row */}
              <div
                className={`p-3 rounded-lg border ${get_status_color(prerequisites.nft_approval.status)}`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="flex items-center space-x-2">
                      {get_status_indicator(prerequisites.nft_approval.status)}
                      <span className="text-white font-medium">
                        NFT Control
                      </span>
                    </div>
                    <p className="text-gray-400 text-xs mt-1">
                      Required for burning and transferring skins
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-white text-sm mb-2">
                      {loading_nft_checks
                        ? "Checking..."
                        : `${prerequisites.nft_approval.current} / ${prerequisites.nft_approval.needed} Approved`}
                    </div>
                    {prerequisites.nft_approval.details &&
                      !loading_nft_checks && (
                        <div className="space-y-1">
                          {Object.entries(
                            prerequisites.nft_approval.details,
                          ).map(([rvmode, approved]) => (
                            <div
                              key={rvmode}
                              className="flex items-center justify-between text-xs"
                            >
                              <span className="text-gray-300 capitalize">
                                {rvmode}:
                              </span>
                              {approved ? (
                                <span className="text-green-400 flex items-center space-x-1">
                                  <span>✓</span>
                                  <span>Approved</span>
                                </span>
                              ) : (
                                <button
                                  onClick={() => set_show_nft_modal(rvmode)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-0.5 rounded text-xs transition-colors"
                                >
                                  Approve
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </div>
              </div>

              {/* Token Allowances Rows */}
              <div className="space-y-2">
                {Object.entries(prerequisites.token_allowances).map(
                  ([token, data]) => (
                    <div
                      key={token}
                      className={`p-3 rounded-lg border ${get_status_color(data.status)}`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="flex items-center space-x-2">
                            {get_status_indicator(data.status)}
                            <span className="text-white font-medium">
                              {token} Spending
                            </span>
                          </div>
                          <p className="text-gray-400 text-xs mt-1">
                            Required for store purchases with {token}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-white text-sm">
                            {dec(data.current, token === "WETH" ? 4 : 0)} /{" "}
                            {dec(data.needed, token === "WETH" ? 4 : 0)}
                          </div>
                          {token !== "SGC" && (
                            <div className="text-gray-400 text-xs">
                              ~${target_usd} USD
                            </div>
                          )}
                          {data.status !== "sufficient" && (
                            <button
                              onClick={() => set_show_token_modal(token)}
                              className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded mt-1 transition-colors"
                            >
                              Approve
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Token Allowance Modals */}
      {show_token_modal && (
        <Modal
          is_open={!!show_token_modal}
          on_close={() => set_show_token_modal(null)}
          title=""
          size="sm"
          className="max-w-2xl"
          floating_close={true}
          hide_titlebar={true}
        >
          <TokenAllowanceModalContent
            title={`${show_token_modal} Token Allowance`}
            token={show_token_modal}
            allowance_target={contractAddress_list.dnapayrouter}
            auto_amount_fn={() => recommended_amounts[show_token_modal]}
            on_close={() => set_show_token_modal(null)}
            vault={vault}
            ciparams={ciparams}
          />
        </Modal>
      )}

      {/* NFT Approval Modals */}
      {show_nft_modal && (
        <Modal
          is_open={!!show_nft_modal}
          on_close={() => set_show_nft_modal(null)}
          title=""
          size="sm"
          className="max-w-2xl"
          floating_close={true}
          hide_titlebar={true}
        >
          <NFTApprovalModalContent
            title={`${show_nft_modal.charAt(0).toUpperCase() + show_nft_modal.slice(1)} NFT Approval`}
            token={show_nft_modal}
            token_addr={skins_rvmode_addrmap[show_nft_modal]}
            contract_addr={contractAddress_list.dnapayrouter}
            on_close={() => set_show_nft_modal(null)}
            vault={vault}
            ciparams={ciparams}
          />
        </Modal>
      )}
    </>
  );
};

